"use client";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";

import Audiotrack from "@mui/icons-material/Audiotrack";
import MusicNote from "@mui/icons-material/MusicNote";
import Box from "@mui/material/Box";

const CalmEqualizerLoader: React.FC = () => {
  const [bars, setBars] = useState<number[]>(Array(7).fill(0.45));
  const [floatingNotes, setFloatingNotes] = useState<
    Array<{ id: number; left: number; icon: React.ReactNode }>
  >([]);

  // Gentle, slow breathing animation for bars
  useEffect(() => {
    const interval = setInterval(() => {
      setBars((prev) =>
        prev.map((oldHeight) => {
          const target = 0.32 + Math.random() * 0.48;
          return oldHeight * 0.78 + target * 0.22; // very smooth and calm lerp
        }),
      );
    }, 220);

    return () => clearInterval(interval);
  }, []);

  // Sparse, gentle floating notes
  useEffect(() => {
    const spawnNote = () => {
      const icons = [
        <MusicNote
          key={0}
          sx={{ fontSize: 24, color: "var(--mui-palette-primary-light)" }}
        />,
        <Audiotrack
          key={1}
          sx={{ fontSize: 22, color: "var(--mui-palette-primary-light)" }}
        />,
      ];

      const newNote = {
        id: Date.now() + Math.random(),
        left: 15 + Math.random() * 70,
        icon: icons[Math.floor(Math.random() * icons.length)],
      };

      setFloatingNotes((prev) => [...prev, newNote]);

      setTimeout(() => {
        setFloatingNotes((prev) => prev.filter((n) => n.id !== newNote.id));
      }, 3600);
    };

    const interval = setInterval(spawnNote, 680); // even more sparse and dreamy

    return () => clearInterval(interval);
  }, []);

  const barColors = [
    "var(--mui-palette-primary-main)",
    "var(--mui-palette-primary-main)",
    "var(--mui-palette-primary-light)",
    "var(--mui-palette-primary-main)",
    "var(--mui-palette-primary-main)",
    "var(--mui-palette-primary-light)",
    "var(--mui-palette-primary-main)",
  ];

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
      }}
    >
      {/* Calm equalizer bars */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "8px",
          height: 72,
          position: "relative",
        }}
      >
        {bars.map((height, index) => (
          <motion.div
            key={index}
            style={{
              width: 13,
              background: `linear-gradient(to top, transparent, ${barColors[index]})`,
              borderRadius: "9999px",
              boxShadow: `0 0 10px color-mix(in srgb, ${barColors[index]} 36%, transparent)`,
            }}
            animate={{
              height: `${height * 100}%`,
            }}
            transition={{
              type: "spring",
              stiffness: 110,
              damping: 32,
              mass: 1.4,
            }}
          />
        ))}

        {/* Gentle floating notes */}
        <AnimatePresence>
          {floatingNotes.map((note) => (
            <motion.div
              key={note.id}
              style={{
                position: "absolute",
                left: `${note.left}%`,
                bottom: 82,
              }}
              initial={{ y: 18, opacity: 0, scale: 0.5 }}
              animate={{
                y: -128,
                opacity: [0, 0.85, 0],
                scale: [0.5, 1.05, 0.65],
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 3.4,
                ease: "easeOut",
              }}
            >
              {note.icon}
            </motion.div>
          ))}
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default CalmEqualizerLoader;
